import React from 'react';
import { Box, Container, Flex, HStack, VStack, Text, Icon, Divider, SimpleGrid, Link } from '@chakra-ui/react';
import { FiMapPin, FiPhone, FiMail, FiClock } from 'react-icons/fi';

// Format a US phone number as (XXX) XXX-XXXX. Falls back to the raw value for
// non-standard/international numbers. Mirrors the helper in SiteLocation.
const formatPhone = (raw) => {
    if (!raw) return '';
    let digits = String(raw).replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
    if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return String(raw);
};

export default function SiteFooter({ config }) {
    const sc = config?.siteContent || {};
    const laundryId = config?.laundryId || '1';
    const laundryName = config?.laundryName || 'Laundry';
    const themeColor = sc.themeColor || 'blue';
    const hours = sc.hours || [];
    const hoursNote = sc.hoursNote || '';

    return (
        <Box bg="gray.800" color="white" pt={{ base: 12, md: 16 }} pb={6}>
            <Container maxW="1200px">
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} mb={8}>
                    <VStack align="flex-start" spacing={3}>
                        <Text fontSize="xl" fontWeight="800" color={`${themeColor}.300`}>{laundryName}</Text>
                        {/* Footer blurb. Prefer a dedicated short line so it doesn't
                            duplicate the full About section: tenant's tagline first,
                            then a truncated About slice only as a fallback, then the
                            generic storefront line. Set an empty tagline to show no
                            blurb at all. */}
                        {(() => {
                            const blurb = (sc.tagline || '').trim()
                                ? sc.tagline.trim()
                                : (sc.about?.description
                                    ? `${sc.about.description.slice(0, 120)}${sc.about.description.length > 120 ? '…' : ''}`
                                    : (sc.address ? 'Your trusted local laundromat.' : ''));
                            return blurb ? (
                                <Text fontSize="sm" color="gray.400" lineHeight="tall">{blurb}</Text>
                            ) : null;
                        })()}
                    </VStack>

                    <VStack align="flex-start" spacing={3}>
                        <Text fontWeight="600" fontSize="sm" color="gray.300" textTransform="uppercase">Contact</Text>
                        <HStack spacing={2} color="gray.400"><Icon as={FiMapPin} boxSize={4} /><Text fontSize="sm">{sc.address ? `${sc.address}, ${sc.city}, ${sc.state} ${sc.zip}` : [sc.city, sc.state].filter(Boolean).join(', ')}</Text></HStack>
                        {sc.phone && <HStack spacing={2} color="gray.400"><Icon as={FiPhone} boxSize={4} /><Text fontSize="sm">{formatPhone(sc.phone)}</Text></HStack>}
                        {sc.email && <HStack spacing={2} color="gray.400"><Icon as={FiMail} boxSize={4} /><Text fontSize="sm">{sc.email}</Text></HStack>}
                    </VStack>

                    <VStack align="flex-start" spacing={3}>
                        <Text fontWeight="600" fontSize="sm" color="gray.300" textTransform="uppercase">Hours</Text>
                        {hoursNote ? (
                            <HStack spacing={2} color="gray.400" align="flex-start">
                                <Icon as={FiClock} boxSize={4} mt={0.5} />
                                <Text fontSize="sm">{hoursNote}</Text>
                            </HStack>
                        ) : (
                            hours.map((h, i) => (
                                <HStack key={i} spacing={2} color="gray.400">
                                    {i === 0 && <Icon as={FiClock} boxSize={4} />}
                                    <Text fontSize="sm">{h.label}: {h.time}</Text>
                                </HStack>
                            ))
                        )}
                    </VStack>
                </SimpleGrid>

                <Divider borderColor="gray.700" />
                <Flex pt={6} direction={{ base: 'column', md: 'row' }} justify="space-between" align="center" gap={2}>
                    <Text fontSize="xs" color="gray.500">© {new Date().getFullYear()} {laundryName}. All rights reserved.</Text>
                    <HStack spacing={4} fontSize="xs" color="gray.500">
                        {String(laundryId) === '1' && (
                            <Link href="/ozone-wash" _hover={{ color: 'white' }}>Ozone Wash</Link>
                        )}
                        <Link href={`/${laundryId}/faq`} _hover={{ color: 'white' }}>FAQ</Link>
                        <Link href="/privacy-policy" _hover={{ color: 'white' }}>Privacy Policy</Link>
                        <Link href="/terms" _hover={{ color: 'white' }}>Terms</Link>
                        <Link href="/terms#sms" _hover={{ color: 'white' }}>SMS Terms</Link>
                    </HStack>
                </Flex>
            </Container>
        </Box>
    );
}
